// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import { getDocument } from '@atlaskit/browser-apis';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useCallback, useEffect, useState } from 'react';

import { DetailViewSpotlight } from './DetailViewSpotlight';

import { HighImpactModal } from './HighImpactModal';

import { HighImpactSpotlightTour } from './HighImpactSpotlightTour';

import ListMergeSpotlightIllustrationDark from '../assets/list-merge-spotlight-dark.gif';

import ListMergeSpotlightIllustration from '../assets/list-merge-spotlight-light.gif';

import { COPY_LIST_SETTINGS_YES } from '../placements/in-app-v2/issue-navigator-placement/constants';

import { type JSX } from 'react/jsx-runtime';

type OnboardingStep = 'detail-view-spotlight' | 'modal' | 'spotlight-tour' | 'dismissed';

type HighImpactOnboardingProps = {
	hasListPreference?: boolean;
	isDetailedView?: boolean;
};

export const HighImpactOnboarding = ({
	hasListPreference,
	isDetailedView,
}: HighImpactOnboardingProps): JSX.Element | null => {
	const [currentStep, setCurrentStep] = useState<OnboardingStep>(
		isDetailedView ? 'detail-view-spotlight' : 'modal',
	);
	const [copyListSettings, setCopyListSettings] = useState(COPY_LIST_SETTINGS_YES);
	const { onAcknowledged, onClosed, onCtaActioned } = usePostOfficeUserInteractions();

	const dismissMessage = useCallback(
		(isTourCompleted = false) => {
			setCurrentStep('dismissed');
			if (isTourCompleted) {
				void onAcknowledged({ interactionIdentifier: 'tourCompleted' });
				return;
			}
			void onClosed({ interactionIdentifier: 'dismiss' });
		},
		[onAcknowledged, onClosed],
	);

	const fireExploreEvent = useCallback(() => {
		// The old clickIdentifier was dynamic (base + a copy-list-settings suffix); the identifier
		// is now a stable literal and the distinguishing info moves to additionalMetadata.
		void onCtaActioned({
			interactionIdentifier: 'exploreFilters',
			additionalMetadata: {
				hasListPreference: Boolean(hasListPreference),
				copiesListSettings: copyListSettings === COPY_LIST_SETTINGS_YES,
			},
		});
	}, [onCtaActioned, hasListPreference, copyListSettings]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				dismissMessage();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [dismissMessage]);

	const theme = useThemeObserver();

	const { fireTrigger: fireExploreListViewButtonTriggerWithCopyListSettings } =
		useFirePostOfficeTrigger({
			triggerId:
				'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-with-copy-list-settings',
		});
	const { fireTrigger: fireExploreListViewButtonTriggerWithoutCopyListSettings } =
		useFirePostOfficeTrigger({
			triggerId:
				'jira-nin-list-merge-modal-onboarding-explore-list-view-button-clicked-without-copy-list-settings',
		});

	const handleExploreListViewButtonClick = useCallback(() => {
		if (copyListSettings === COPY_LIST_SETTINGS_YES) {
			fireExploreListViewButtonTriggerWithCopyListSettings({ isActive: true });
		} else {
			fireExploreListViewButtonTriggerWithoutCopyListSettings({ isActive: true });
		}
		setCurrentStep('spotlight-tour');
		fireExploreEvent();
	}, [
		copyListSettings,
		fireExploreListViewButtonTriggerWithCopyListSettings,
		fireExploreListViewButtonTriggerWithoutCopyListSettings,
		fireExploreEvent,
	]);

	const isDarkMode = theme.colorMode === 'dark';

	const listMergeSpotlightIllustration = isDarkMode
		? ListMergeSpotlightIllustrationDark
		: ListMergeSpotlightIllustration;

	switch (currentStep) {
		case 'detail-view-spotlight':
			return (
				<DetailViewSpotlight
					setNextStep={() => setCurrentStep('spotlight-tour')}
					onDismissed={dismissMessage}
					illustration={listMergeSpotlightIllustration}
				/>
			);
		case 'modal':
			return (
				<HighImpactModal
					hasListPreference={hasListPreference}
					onClose={() => dismissMessage()}
					onExploreListViewButtonClick={handleExploreListViewButtonClick}
					copyListSettings={copyListSettings}
					setCopyListSettings={setCopyListSettings}
				/>
			);
		case 'spotlight-tour':
			return <HighImpactSpotlightTour onDismiss={dismissMessage} />;
		default:
			return null;
	}
};
